import { Button, Checkbox, Form, Input, InputNumber, Modal, Select, Space, Spin, Tag, Typography } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import React, { ChangeEvent, ReactElement, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import BorrowerCategoryEnum from '~Api/Application/BorrowerCategoryEnum';
import BorrowerTypeEnum from '~Api/Application/BorrowerTypeEnum';
import GenderEnum from '~Api/Application/GenderEnum';
import IApplication from '~Api/Application/IApplication';
import IBorrower from '~Api/Application/IBorrower';
import IncomeTypeEnum from '~Api/Application/IncomeTypeEnum';
import LicenceStateEnum from '~Api/Application/LicenceStateEnum';
import MaritalStatusEnum from '~Api/Application/MaritalStatusEnum';
import TrusteeTypeEnum from '~Api/Application/TrusteeTypeEnum';
import IDealBorrower from '~Api/Deal/IDealBorrower';
import {
    applicationBorrowerDeleteAction,
    applicationBorrowerGetAction,
    applicationBorrowerValueSetAction,
    applicationGetAction,
} from '~Applications/actions';
import {
    applicationBorrowerSelector,
    applicationSelector,
} from '~Applications/selectors';
import { countriesGetAction } from '~Countries/actions';
import ICountry from '~Countries/ICountry';
import { countriesSelector } from '~Countries/selectors';
import { dealBorrowerValueSetAction } from '~Deals/actions';
import { applicationBorrowerDealBorrowerSelector } from '~Deals/selectors';
import { IGlobalState } from '~reducer';
import DatePicker from '~UI/DatePicker';
import Layout from './Layout';
import ResidencyStatusEnum from '~Api/Application/ResidencyStatusEnum';
import { DefaultOptionType } from 'antd/lib/select';
import BorrowerDocTypeEnum from '~Api/Application/BorrowerDocTypeEnum';

const borrowerDocTypeLabels: IDictionary<string> = {
    [BorrowerDocTypeEnum.Full]: 'Full',
    [BorrowerDocTypeEnum.Low]: 'Low',
};

export default function Borrower(): ReactElement {
    const {
        applicationBorrowerUuid,
        applicationUuid,
    } = useParams<{
        applicationBorrowerUuid: string;
        applicationUuid: string;
    }>();

    const application: IApplication = useSelector((
        state: IGlobalState,
    ): IApplication => applicationSelector(
        state,
        applicationUuid,
    ));
    const applicationBorrower: IBorrower = useSelector((
        state: IGlobalState,
    ): IBorrower => applicationBorrowerSelector(
        state,
        applicationUuid,
        applicationBorrowerUuid,
    ));
    const countries: ICountry[] = useSelector(countriesSelector);
    const dealBorrower: IDealBorrower = useSelector((
        state: IGlobalState,
    ): IDealBorrower => applicationBorrowerDealBorrowerSelector(
        state,
        applicationUuid,
        applicationBorrowerUuid,
    ));

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!application) {
            dispatch(applicationGetAction(applicationUuid));
        }
    }, [
        dispatch,
        application,
        applicationUuid,
    ]);

    useEffect((): void => {
        if (!applicationBorrower) {
            dispatch(applicationBorrowerGetAction(applicationUuid, applicationBorrowerUuid));
        }
    }, [
        dispatch,
        applicationBorrower,
        applicationBorrowerUuid,
        applicationUuid,
    ]);

    useEffect((): void => {
        if (!countries) {
            dispatch(countriesGetAction());
        }
    }, [
        dispatch,
        countries,
    ]);

    const applicationBorrowerValueSet: (
        key: keyof IBorrower,
        value: boolean | number | string,
    ) => void = useCallback(
        (
            key: keyof IBorrower,
            value: boolean | number | string,
        ): void => {
            dispatch(applicationBorrowerValueSetAction(applicationUuid, applicationBorrowerUuid, key, value));
        },
        [
            applicationBorrowerUuid,
            applicationUuid,
            dispatch,
        ],
    );

    const dealBorrowerValueSet: (
        dealBorrowerUuid: string,
        key: keyof IDealBorrower,
        value: boolean | number | string,
    ) => void = useCallback(
        (
            dealBorrowerUuid: string,
            key: keyof IDealBorrower,
            value: boolean | number | string,
        ): void => {
            dispatch(dealBorrowerValueSetAction(dealBorrowerUuid, key, value));
        },
        [
            dispatch,
        ],
    );

    const borrowerDelete: () => void = useCallback((): void => {
        dispatch(applicationBorrowerDeleteAction(applicationUuid, applicationBorrowerUuid));
    }, [
        applicationBorrowerUuid,
        applicationUuid,
        dispatch,
    ]);

    const onChangeCategory: (value: BorrowerCategoryEnum) => void = useCallback(
        (value: BorrowerCategoryEnum): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'category', value);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeType: (value: BorrowerTypeEnum) => void = useCallback(
        (value: BorrowerTypeEnum): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'type', value);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeAbn: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            if (/[^0-9]/.test(event.target.value)) {
                return;
            }

            dealBorrowerValueSet(dealBorrower.uuid, 'abn', event.target.value.substr(0, 11));
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeAddress: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'address', event.target.value.substr(0, 255));
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeBusinessName: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'businessName', event.target.value.substr(0, 100));
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeCreditScore: (value: number) => void = useCallback(
        (value: number): void => {
            if (value < 0 || value > 1200) {
                return;
            }

            applicationBorrowerValueSet('creditScore', value);
        },
        [
            applicationBorrowerValueSet,
        ],
    );

    const onChangeDependentNumber: (value: number) => void = useCallback(
        (value: number): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'dependentNumber', value);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeDob: (date: Dayjs) => void = useCallback(
        (date: Dayjs): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'dob', date ? date.format('YYYY-MM-DD') : null);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeDocType: (value: BorrowerDocTypeEnum) => void = useCallback(
        (value: BorrowerDocTypeEnum): void => {
            applicationBorrowerValueSet('docType', value);
        },
        [
            applicationBorrowerValueSet,
        ],
    );

    const onChangeEmail: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'email', event.target.value.substr(0, 100));
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeFirstName: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'firstName', event.target.value.substr(0, 50));
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeGender: (value: GenderEnum) => void = useCallback(
        (value: GenderEnum): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'gender', value);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeIncomeType: (value: IncomeTypeEnum) => void = useCallback(
        (value: IncomeTypeEnum): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'incomeType', value);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeJobTitle: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'jobTitle', event.target.value.substr(0, 100));
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeLastName: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'lastName', event.target.value.substr(0, 50));
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeLicenceNumber: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'licenceNumber', event.target.value.substr(0, 20));
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeLicenceState: (value: LicenceStateEnum) => void = useCallback(
        (value: LicenceStateEnum): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'licenceState', value);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeMaritalStatus: (value: MaritalStatusEnum) => void = useCallback(
        (value: MaritalStatusEnum): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'maritalStatus', value);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeMiddleName: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'middleName', event.target.value.substr(0, 255));
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeMonthlyGrossExpenses: (value: number) => void = useCallback(
        (value: number): void => {
            if (value < 0 || value > 99999999) {
                return;
            }

            dealBorrowerValueSet(dealBorrower.uuid, 'monthlyGrossExpenses', value);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeMonthlyGrossTurnover: (value: number) => void = useCallback(
        (value: number): void => {
            if (value < 0 || value > 99999999) {
                return;
            }

            dealBorrowerValueSet(dealBorrower.uuid, 'monthlyGrossTurnover', value);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeMonthlyHouseholdExpenses: (value: number) => void = useCallback(
        (value: number): void => {
            if (value < 0 || value > 99999999) {
                return;
            }

            dealBorrowerValueSet(dealBorrower.uuid, 'monthlyHouseholdExpenses', value);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeOtherIncomeType: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'otherIncomeType', event.target.value.substr(0, 100));
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeOnTitle: (event: CheckboxChangeEvent) => void = useCallback(
        (event: CheckboxChangeEvent): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'onTitle', event.target.checked);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangePassportCountry: (value: string) => void = useCallback(
        (value: string): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'passportCountry', value.substr(0, 3));
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangePassportNumber: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'passportNumber', event.target.value.substr(0, 20));
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangePhone: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'phone', event.target.value.substr(0, 20));
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeResidencyStatus: (value: ResidencyStatusEnum) => void = useCallback(
        (value: ResidencyStatusEnum): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'residencyStatus', value);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeTotalAssets: (value: number) => void = useCallback(
        (value: number): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'totalAssets', value);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeTotalLiabilities: (value: number) => void = useCallback(
        (value: number): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'totalLiabilities', value);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeTrustName: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            dealBorrowerValueSet(dealBorrower.uuid, 'trustName', event.target.value.substr(0, 100));
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onChangeYearlyIncome: (value: number) => void = useCallback(
        (value: number): void => {
            if (value < 0 || value > 99999999) {
                return;
            }

            dealBorrowerValueSet(dealBorrower.uuid, 'yearlyIncome', value);
        },
        [
            dealBorrower,
            dealBorrowerValueSet,
        ],
    );

    const onClickDelete: () => void = useCallback((): void => {
        Modal.confirm({
            content: 'Are you sure you want to delete this borrower?',
            okText: 'Delete',
            okType: 'danger',
            onOk: borrowerDelete,
            title: 'Delete',
        });
    }, [
        borrowerDelete,
    ]);

    if (!application || !applicationBorrower || !countries || !dealBorrower) {
        return (
            <Layout applicationUuid={applicationUuid} section='borrower'>
                <Spin />
            </Layout>
        );
    }

    const actionsBlock: JSX.Element = application.primaryBorrowerUuid === applicationBorrower.uuid ? (
        <Tag>Primary</Tag>
    ) : (
        <Button className='delete-borrower' danger={true} onClick={onClickDelete}>Delete</Button>
    );

    const borrowerTypeOptions: DefaultOptionType[] = [
        {
            label: 'Company',
            value: BorrowerTypeEnum.Company,
        },
        {
            label: 'Individual',
            value: BorrowerTypeEnum.Individual,
        },
        ...(dealBorrower.category !== BorrowerCategoryEnum.Trustee ? [
            {
                label: 'Trust',
                value: BorrowerTypeEnum.Trust,
            },
        ] : []),
    ];

    const companyBlock: JSX.Element = dealBorrower.type === BorrowerTypeEnum.Company && (
        <React.Fragment>
            <Form.Item className='business-name' label='Company Name'>
                <Input onChange={onChangeBusinessName} value={dealBorrower.businessName} />
            </Form.Item>
            <Form.Item className='abn' label='ACN or ABN'>
                <Input onChange={onChangeAbn} value={dealBorrower.abn} />
            </Form.Item>
            <Form.Item className='monthly-gross-turnover' label='Monthly Gross Turnover'>
                <InputNumber
                    addonBefore='$'
                    max={99999999}
                    min={0}
                    onChange={onChangeMonthlyGrossTurnover}
                    value={dealBorrower.monthlyGrossTurnover}
                />
            </Form.Item>
            <Form.Item className='monthly-gross-expenses' label='Monthly Gross Expenses'>
                <InputNumber
                    addonBefore='$'
                    max={99999999}
                    min={0}
                    onChange={onChangeMonthlyGrossExpenses}
                    value={dealBorrower.monthlyGrossExpenses}
                />
            </Form.Item>
            <Form.Item className='total-assets' label='Total Assets'>
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeTotalAssets}
                    value={dealBorrower.totalAssets}
                />
            </Form.Item>
            <Form.Item className='total-liabilities' label='Total Liabilities'>
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeTotalLiabilities}
                    value={dealBorrower.totalLiabilities}
                />
            </Form.Item>
            <Form.Item className='credit-score' label='Credit Score'>
                <InputNumber
                    max={1200}
                    min={0}
                    onChange={onChangeCreditScore}
                    value={applicationBorrower.creditScore}
                />
            </Form.Item>
        </React.Fragment>
    );

    const borrowerLicenseStateOptions: DefaultOptionType[] = _.values(LicenceStateEnum).map(
        (stateCode: string): DefaultOptionType => ({
            label: stateCode,
            value: stateCode,
        }),
    );

    const borrowerPassportCountryOptions: DefaultOptionType[] = countries.map(
        (country: ICountry): DefaultOptionType => ({
            label: country.name,
            value: country.iso3,
        }),
    );

    const jobTitleBlock: JSX.Element = [
        IncomeTypeEnum.PaygFullTime,
        IncomeTypeEnum.PaygPartTime,
    ].includes(dealBorrower.incomeType) && (
        <Form.Item className='job-title' label='Job Title'>
            <Input onChange={onChangeJobTitle} value={dealBorrower.jobTitle} />
        </Form.Item>
    );

    const otherIncomeTypeBlock: JSX.Element = dealBorrower.incomeType === IncomeTypeEnum.Other && (
        <Form.Item className='other-income-type' label='Other Income Details'>
            <Input onChange={onChangeOtherIncomeType} value={dealBorrower.otherIncomeType} />
        </Form.Item>
    );

    const selfEmployedBlock: JSX.Element = dealBorrower.incomeType === IncomeTypeEnum.SelfEmployed && (
        <React.Fragment>
            <Form.Item className='business-name' label='Business Name'>
                <Input onChange={onChangeBusinessName} value={dealBorrower.businessName} />
            </Form.Item>
            <Form.Item className='abn' label='ABN'>
                <Input onChange={onChangeAbn} value={dealBorrower.abn} />
            </Form.Item>
        </React.Fragment>
    );

    const individualBlock: JSX.Element = dealBorrower.type === BorrowerTypeEnum.Individual && (
        <React.Fragment>
            <Form.Item className='first-name' label='First Name'>
                <Input onChange={onChangeFirstName} value={dealBorrower.firstName} />
            </Form.Item>
            <Form.Item className='middle-name' label='Middle Name(s)'>
                <Input onChange={onChangeMiddleName} value={dealBorrower.middleName} />
            </Form.Item>
            <Form.Item className='last-name' label='Last Name'>
                <Input onChange={onChangeLastName} value={dealBorrower.lastName} />
            </Form.Item>
            <Form.Item className='phone' label='Phone'>
                <Input onChange={onChangePhone} value={dealBorrower.phone} />
            </Form.Item>
            <Form.Item className='email' label='Email'>
                <Input onChange={onChangeEmail} value={dealBorrower.email} />
            </Form.Item>
            <Form.Item className='dob' label='DOB'>
                <DatePicker
                    format='DD/MM/YYYY'
                    onChange={onChangeDob}
                    value={dealBorrower.dob ? dayjs(dealBorrower.dob) : null}
                />
            </Form.Item>
            <Form.Item className='gender' label='Gender'>
                <Select onChange={onChangeGender} value={dealBorrower.gender}>
                    <Select.Option value={GenderEnum.Female}>Female</Select.Option>
                    <Select.Option value={GenderEnum.Male}>Male</Select.Option>
                </Select>
            </Form.Item>
            <Form.Item className='licence-state' label='Licence State'>
                <Select
                    onChange={onChangeLicenceState}
                    options={borrowerLicenseStateOptions}
                    value={dealBorrower.licenceState}
                />
            </Form.Item>
            <Form.Item className='licence-number' label='Licence Number'>
                <Input onChange={onChangeLicenceNumber} value={dealBorrower.licenceNumber} />
            </Form.Item>
            <Form.Item className='passport-country' label='Passport Country'>
                <Select
                    onChange={onChangePassportCountry}
                    options={borrowerPassportCountryOptions}
                    value={dealBorrower.passportCountry}
                />
            </Form.Item>
            <Form.Item className='passport-number' label='Passport Number'>
                <Input onChange={onChangePassportNumber} value={dealBorrower.passportNumber} />
            </Form.Item>
            <Form.Item className='residency-status' label='Residency Status'>
                <Select onChange={onChangeResidencyStatus} value={dealBorrower.residencyStatus}>
                    <Select.Option value={ResidencyStatusEnum.Resident}>Resident</Select.Option>
                    <Select.Option value={ResidencyStatusEnum.NonResident}>Non-Resident</Select.Option>
                </Select>
            </Form.Item>
            <Form.Item className='address' label='Address'>
                <Input onChange={onChangeAddress} value={dealBorrower.address} />
            </Form.Item>
            <Form.Item className='dependent-number' label='Number of Dependents'>
                <Select onChange={onChangeDependentNumber} value={dealBorrower.dependentNumber}>
                    {_.times(11, (n: number): ReactElement => <Select.Option key={n} value={n}>{n}</Select.Option>)}
                </Select>
            </Form.Item>
            <Form.Item className='marital-status' label='Marital Status'>
                <Select onChange={onChangeMaritalStatus} value={dealBorrower.maritalStatus}>
                    <Select.Option value={MaritalStatusEnum.Single}>Single</Select.Option>
                    <Select.Option value={MaritalStatusEnum.Married}>Married</Select.Option>
                    <Select.Option value={MaritalStatusEnum.DeFacto}>De Facto</Select.Option>
                    <Select.Option value={MaritalStatusEnum.Separated}>Separated</Select.Option>
                    <Select.Option value={MaritalStatusEnum.Divorced}>Divorced</Select.Option>
                </Select>
            </Form.Item>
            <Form.Item className='income-type' label='Income Type'>
                <Select onChange={onChangeIncomeType} value={dealBorrower.incomeType}>
                    <Select.Option value={IncomeTypeEnum.PaygFullTime}>PAYG - Full Time</Select.Option>
                    <Select.Option value={IncomeTypeEnum.PaygPartTime}>PAYG - Part Time</Select.Option>
                    <Select.Option value={IncomeTypeEnum.SelfEmployed}>Self Employed</Select.Option>
                    <Select.Option value={IncomeTypeEnum.Other}>Other</Select.Option>
                </Select>
            </Form.Item>
            {otherIncomeTypeBlock}
            {jobTitleBlock}
            {selfEmployedBlock}
            <Form.Item className='yearly-income' label='Yearly Income'>
                <InputNumber
                    addonBefore='$'
                    max={99999999}
                    min={0}
                    onChange={onChangeYearlyIncome}
                    value={dealBorrower.yearlyIncome}
                />
            </Form.Item>
            <Form.Item className='monthly-household-expenses' extra='Monthly' label='Household Expenses'>
                <InputNumber
                    addonBefore='$'
                    max={99999999}
                    min={0}
                    onChange={onChangeMonthlyHouseholdExpenses}
                    value={dealBorrower.monthlyHouseholdExpenses}
                />
            </Form.Item>
            <Form.Item className='total-assets' label='Total Assets'>
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeTotalAssets}
                    value={dealBorrower.totalAssets}
                />
            </Form.Item>
            <Form.Item className='total-liabilities' label='Total Liabilities'>
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeTotalLiabilities}
                    value={dealBorrower.totalLiabilities}
                />
            </Form.Item>
            <Form.Item className='credit-score' label='Credit Score'>
                <InputNumber
                    max={1200}
                    min={0}
                    onChange={onChangeCreditScore}
                    value={applicationBorrower.creditScore}
                />
            </Form.Item>
        </React.Fragment>
    );

    const trusteeNameBlock: JSX.Element = dealBorrower.type === BorrowerTypeEnum.Trust && dealBorrower.trusteeName && (
        <Form.Item className='trustee-name' label='Trustee Name'>
            <Input disabled={true} value={dealBorrower.trusteeName} />
        </Form.Item>
    );

    const trusteeTypeBlock: JSX.Element = dealBorrower.type === BorrowerTypeEnum.Trust && dealBorrower.trusteeType && (
        <Form.Item className='trustee-type' label='Trustee Type'>
            <Select disabled={true} value={dealBorrower.trusteeType}>
                <Select.Option value={TrusteeTypeEnum.Company}>Company</Select.Option>
                <Select.Option value={TrusteeTypeEnum.Individual}>Individual</Select.Option>
            </Select>
        </Form.Item>
    );

    const trustBlock: JSX.Element = dealBorrower.type === BorrowerTypeEnum.Trust && (
        <React.Fragment>
            <Form.Item className='trust-name' label='Trust Name'>
                <Input onChange={onChangeTrustName} value={dealBorrower.trustName} />
            </Form.Item>
            <Form.Item className='abn' label='ACN or ABN'>
                <Input onChange={onChangeAbn} value={dealBorrower.abn} />
            </Form.Item>
            {trusteeTypeBlock}
            {trusteeNameBlock}
            <Form.Item className='total-assets' label='Total Assets'>
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeTotalAssets}
                    value={dealBorrower.totalAssets}
                />
            </Form.Item>
            <Form.Item className='total-liabilities' label='Total Liabilities'>
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeTotalLiabilities}
                    value={dealBorrower.totalLiabilities}
                />
            </Form.Item>
        </React.Fragment>
    );

    const borrowerDocTypeOptions: DefaultOptionType[] = _.keys(borrowerDocTypeLabels).map(
        (docType: BorrowerDocTypeEnum): DefaultOptionType => ({
            label: borrowerDocTypeLabels[docType],
            value: docType,
        }),
    );

    return (
        <Layout applicationUuid={applicationUuid} section='borrower'>
            <Space className='actions'>
                {actionsBlock}
            </Space>
            <Typography.Title level={2}>Borrower</Typography.Title>
            <Form.Item className='category' label='Category'>
                <Select onChange={onChangeCategory} value={dealBorrower.category}>
                    <Select.Option value={BorrowerCategoryEnum.Borrower}>Borrower</Select.Option>
                    <Select.Option value={BorrowerCategoryEnum.Guarantor}>Guarantor</Select.Option>
                    <Select.Option value={BorrowerCategoryEnum.Trustee}>Trustee</Select.Option>
                </Select>
            </Form.Item>
            <Form.Item className='type' label='Type'>
                <Select
                    onChange={onChangeType}
                    options={borrowerTypeOptions}
                    value={dealBorrower.type}
                />
            </Form.Item>
            {companyBlock}
            {individualBlock}
            {trustBlock}
            <Form.Item className='doc-type' label='Doc Type'>
                <Select
                    onChange={onChangeDocType}
                    options={borrowerDocTypeOptions}
                    value={applicationBorrower.docType}
                />
            </Form.Item>
            <Form.Item className='on-title' label='On Title'>
                <Checkbox checked={dealBorrower.onTitle} onChange={onChangeOnTitle} />
            </Form.Item>
        </Layout>
    );
}
